import React, { useState, useEffect, useMemo } from 'react';
import { QuestionDisplayProps } from '../types';
import { MathQuestionChart, useHasMathVisualization } from './math/MathQuestionChart';
import { MathRenderer } from './math/MathRenderer';
import { MarkdownMathRenderer } from './math/MarkdownMathRenderer';
import { isAnswerCorrect } from '../utils/answerMatching';
import { optionSeed, shuffledOptionOrder } from '../utils/optionOrder';

/**
 * QuestionDisplay Component
 *
 * Enhanced quiz question renderer with mathematical visualization capabilities.
 * Supports multiple-choice and text-based questions with intelligent chart generation
 * for mathematical content.
 *
 * Core Features:
 * - Displays question text with appropriate input controls based on question type
 * - Handles multiple-choice options with selectable interface
 * - Supports text input fields for short answer questions
 * - Manages user input state and answer submission
 * - Shows correctness feedback and highlights correct answers during review
 * - Displays explanations and worked solutions when in review mode
 *
 * Mathematical Visualization Features:
 * - Automatic detection of mathematical content suitable for graphing
 * - Interactive equation plotting for linear algebra and graphable equations
 * - Visual enhancement for learning mathematical concepts
 * - Seamless integration without disrupting existing question flow
 *
 * The component maintains internal state for user input while accepting
 * external control through props for quiz navigation and answer validation.
 * Mathematical visualizations are automatically enabled when applicable content is detected.
 */
export const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  questionResult,
  userAnswer = '',
  onAnswerSubmit,
  showResult = false,
  onMoveToNext,
  shuffleSeed,
}) => {
  const question = questionResult.question;
  const isCorrect = questionResult.isCorrect;
  const [currentAnswer, setCurrentAnswer] = useState<string | number | string[]>(userAnswer || '');

  // Check if this question can benefit from mathematical visualization
  const { hasVisualization } = useHasMathVisualization(question);


  // Options can be either string array or object with letter keys
  const { optionEntries, isMultipleChoice } = useMemo(() => {
    const isMC = question.questionType === 'multiple-choice' || question.questionType === 'multiple_choice';

    if (!isMC || !question.optionsJson) {
      return { optionEntries: [], isMultipleChoice: isMC };
    }

    // Handle both array and object formats
    if (Array.isArray(question.optionsJson)) {
      // If it's an array, create letter keys (A, B, C, D)
      // Each option keeps its stored letter as its value; only the display order
      // and the displayed label are shuffled, so answer matching is unaffected (#150).
      const options = question.optionsJson;
      const seed = optionSeed(shuffleSeed, question.questionId);
      const entries = shuffledOptionOrder(options.length, seed).map((index, position) => ({
        key: String.fromCharCode(65 + index),
        label: String.fromCharCode(65 + position),
        text: options[index]
      }));
      return { optionEntries: entries, isMultipleChoice: isMC };
    } else if (typeof question.optionsJson === 'object') {
      // If it's an object like {"A": "text", "B": "text"}
      const entries = Object.entries(question.optionsJson).map(([key, text]) => ({
        key: key,
        label: key,
        text: text
      }));
      return { optionEntries: entries, isMultipleChoice: isMC };
    }

    return { optionEntries: [], isMultipleChoice: isMC };
  }, [question.optionsJson, question.questionType, question.questionId, shuffleSeed]);

  // Clear inputs whenever the question changes
  useEffect(() => {
    setCurrentAnswer(userAnswer || '');
  }, [question.questionId, userAnswer]);

  /**
   * Handles changes to the text input field.
   * Updates local state and calls the parent's onAnswerSubmit callback.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentAnswer(value);
    onAnswerSubmit(value);
  };

  /**
   * Handles selection of a multiple-choice option.
   * Stores the option key for display but submits the key to the backend.
   */
  const handleOptionSelect = (optionKey: string) => {
    setCurrentAnswer(optionKey); // Store the key for UI state
    onAnswerSubmit(optionKey); // Submit the key to the backend
  };

  /**
   * Handles keyboard events in the text input field.
   * Submits the answer and advances to the next question when Enter is pressed.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentAnswer && !showResult) {
      onAnswerSubmit(currentAnswer);
      if (onMoveToNext) {
        onMoveToNext();
      }
    }
  };


  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      {/* TODO: Add question title here */}
      <div className="text-xl font-semibold mb-4">
        <MarkdownMathRenderer>{question.questionDescription}</MarkdownMathRenderer>
      </div>

      {hasVisualization && (
        <div className="mb-6">
          <MathQuestionChart
            question={question}
            xRange={{ min: -6, max: 6 }}
            yRange={undefined} // Auto-calculate
          />
        </div>
      )}


      {/* Multiple Choice Question */}
      {isMultipleChoice && optionEntries.length > 0 && (
        <div className="space-y-3">
          {optionEntries.map((option) => (
            <div
              key={option.key}
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${currentAnswer === option.key
                ? 'border-secondary-500 bg-secondary-50'
                : 'border-gray-300 hover:border-secondary-300'
                } ${showResult && currentAnswer === option.key
                  ? isCorrect
                    ? 'bg-green-100 border-green-500'
                    : 'bg-red-100 border-red-500'
                  : ''
                } ${showResult && isAnswerCorrect(option.key, question.answerKey, question.optionsJson) && currentAnswer !== option.key
                  ? 'bg-green-50 border-green-500'
                  : ''
                }`}
              onClick={() => !showResult && handleOptionSelect(option.key)}
            >
              <span className="font-medium">{option.label}.</span> <MathRenderer math={option.text} display="inline" />
            </div>
          ))}
        </div>
      )}

      {/* Text Input Question, also used for a multiple choice question with no options (#130) */}
      {(question.questionType === 'short_answer' || (isMultipleChoice && optionEntries.length === 0)) && (
        <div className="space-y-4">
          <input
            type="text"
            value={currentAnswer as string}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={showResult}
            className={`w-full p-3 border rounded-lg ${showResult
              ? isCorrect
                ? 'bg-green-50 border-green-500'
                : 'bg-red-50 border-red-500'
              : 'border-gray-300'
              }`}
            placeholder="Enter your answer..."
          />
        </div>
      )}

      {/* Results Display */}
      {showResult && (
        <div className={`mt-16 p-4 rounded-lg ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
          <p className={`font-semibold ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
            Worked Solution
          </p>
          <div className="mt-2">
            {question.explanation && (
              <div className="mt-2">
                <MarkdownMathRenderer display="inline">
                  {question.explanation}
                </MarkdownMathRenderer>
              </div>
            )}
          </div>

          {/* Worked Solution in Collapsible Section */}
          {question.workedSolution && (
            <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
              <div className="prose prose-sm max-w-none">
                <MarkdownMathRenderer>
                  {question.workedSolution}
                </MarkdownMathRenderer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
